import { computed, ref } from 'vue';
import { useRouter } from 'vue-router/composables';
import i18n from '@/plugins/i18n';
import { performAction } from '@/services/vue-utils';
import store from '@/store/store';
import { startAutoUpdates, stopAutoUpdates } from '@/services/autoupdater';
import { startTodayUpdatesAsync } from '@/services/now-updater';
import UserAccount from '@/model/UserAccount';

export default function useAccountSwitcher(router = useRouter()) {
  const newAccountName = ref(null as string | null);
  const accountName = computed(() => store.state.settings.account.accountName ?? newAccountName.value ?? '');

  const newUserName = ref(null as string | null);
  const userName = computed(() => store.state.settings.account.username || newUserName.value || '');

  const currentAccountId = computed(() => store.state.settings.account.id);

  const switchAccount = async (a: UserAccount) => {
    newAccountName.value = a.accountName;
    newUserName.value = store.state.settings.account.username ?? '';

    await performAction(
      null,
      i18n.tc('error.load_data'),
      async () => {
        await stopAutoUpdates();
        store.dispatch('switchAccount', { id: a.id, token: a.token, userName: newUserName.value });
        // an entity route (numeric :id, e.g. /newsletter/123) belongs to the
        // previous account - reload on its parent list instead; other routes
        // reload in place so the user can inspect the same page across accounts;
        // location-based like router.go(0) was, so unsaved-changes protection
        // stays the native beforeunload prompt and no route-leave guard can stall
        const route = router.currentRoute;
        if (Number(route.params.id) > 0) {
          window.location.replace(router.resolve(route.path.replace(/\/[^/]+$/, '')).href);
        } else if (route.path === '/accounts') {
          // overview is not a per-account page — open the selected restaurant
          window.location.replace(router.resolve('/dashboard').href);
        } else {
          window.location.reload();
        }
        await startTodayUpdatesAsync();
        await startAutoUpdates();
      },
    );
  };

  return {
    switchAccount,
    accountName,
    userName,
    currentAccountId,
  };
}

export function hasMultipleAccounts() {
  return (store.getters.userAccounts as UserAccount[]).length > 1;
}

export function defaultHomePath() {
  return hasMultipleAccounts() ? '/accounts' : '/';
}
